import * as React from 'react'
import { ReactElement, RefObject, useEffect, useMemo, useRef, useState } from 'react'
import { DialogContent, makeStyles, mergeClasses, MessageBar, MessageBarBody, tokens } from '@fluentui/react-components'
import useAxios from '../../../../hooks/useAxios'
import { useTranslation } from 'react-i18next'
import useViewport, { Viewport } from '../../../../hooks/useViewport'
import { HammockFormDialogProps } from '../HammockFormDialog'
import * as Yup from 'yup'
import FormikComboboxField from '../../../../components/Formik/FormikComboboxField'
import { TimeMutationDto, TimeOverviewDto } from '../../../../config/registrationConfig'
import { BaseMutableOverviewDialogProps } from '../../../../pages/BaseMutableOverview/BaseMutableOverview'
import FileCombobox, { FileComboboxProps } from '../../../../components/Combobox/FileCombobox'
import EnumMapCombobox, { EnumMapComboboxProps } from '../../../../components/Combobox/EnumMapCombobox'
import { useSettings } from '../../../../hooks/useSettings'
import useModules from '../../../../hooks/module/useModules'
import { useTimeCategoriesSetting } from '../../../../hooks/file/useTimeCategoriesSetting'
import { useTimeDivisorSetting } from '../../../../hooks/file/useTimeDivisorSetting'
import { FileRateType, ModuleType, TimeCategory } from '../../../../context/GlobalContext'
import { InvoiceStatus } from '../../../../config/finance/invoiceConfig'
import { BudgetMonitoringDto, IFileDto, RateDto } from '../../../../config/fileConfig'
import { useFormikContext } from 'formik'
import FormikTextareaField from '../../../../components/Formik/FormikTextareaField'
import { Billable } from '../../../../config/registration/timeConfig'
import OverflowButtonPanel from '../../../../components/Overflow/OverflowButtonPanel'
import {
    formatInlineCurrency,
    formatTimeInMinutes,
    multiplyTime,
    multiplyTimeNumber,
    parseNumber,
    parseTime,
    parseTimeInHours,
    parseTimeInMinutes,
} from '../../../../helpers/numberHelper'
import UserCombobox from '../../../../components/Combobox/UserCombobox'
import useRoles, { rolesWriteAllTimes } from '../../../../hooks/useRoles'
import { SubComboboxProps } from '../../../../components/Combobox/BaseCombobox'
import { UserListItemDto } from '../../../../config/userConfig'
import FormikDurationField from '../../../../components/Formik/FormikDurationField'
import { useFormikFieldStyles } from '../../../../components/Formik/FormikField'
import { usePrevious } from '@fluentui/react-hooks'
import { IRefObject } from '@fluentui/utilities'
import { ITextField } from '@fluentui/react'
import { BudgetMonitoringModuleDto, useFileModule } from '../../../../hooks/module/useFileModule'
import { BaseTierDto } from '../../../../config/finance/financeConfig'
import useCurrency from '../../../../hooks/useCurrency'
import { resolveRate } from '../../../../helpers/rateHelper'
import FormikMoneyField from '../../../../components/Formik/FormikMoneyField'
import BillableCombobox, { BillableComboboxProps } from '../../../../components/Combobox/BillableCombobox'
import FormContainer from '../../Form/FormContainer'
import useDerivedFormValues from '../../../hooks/useDerivedFormValues'
import FormikDatePickerField from '../../../../components/Formik/FormikDatePickerField'

const useStyles = makeStyles({
    billable: {
        flexGrow: 2,
    },
    rateRow: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRow: {
        display: 'grid',
        gridTemplateColumns: `2fr 3fr`,
        gap: tokens.spacingHorizontalS,
    },
    dateRowSmall: {
        display: 'grid',
        gridTemplateColumns: `1fr 1fr`,
        gap: tokens.spacingHorizontalS,
    },
})

export const getValidationSchema = (hasTimeCategoryModule: boolean, translate: (key: string, options?: any) => string): Yup.ObjectSchema<any> => {
    return Yup.object().shape({
        category: hasTimeCategoryModule
            ? Yup.string().label(translate('time.category.label')).required()
            : Yup.string().label(translate('time.category.label')).nullable(),
        description: Yup.string().label(translate('time.description')).required(),
        fileId: Yup.string().uuid().label(translate('time.fileid')).required(),
        duration: Yup.string()
            .label(translate('time.duration.label'))
            .required()
            .test('is-valid', translate('time.duration.isvalid'), value => !isNaN(parseTimeInMinutes(value)))
            .test('is-valid-minutes', translate('time.duration.isvalidminutes'), value => parseTime(value).minutes < 60)
            .test('is-less-than-a-minute', translate('time.duration.islessminute'), value => parseTimeInMinutes(value) >= 1)
            .test('is-more-than-a-day', translate('time.duration.ismoreday'), value => parseTimeInMinutes(value) <= 24 * 60),
        rate: Yup.string().label(translate('time.rate')).required(),
        userId: Yup.string().uuid().label(translate('time.userid')).required(),
        date: Yup.string().label(translate('time.date')).required(),
        billable: Yup.string().label(translate('time.billable')).required(),
    })
}

const getRateValue = (initialValues: TimeMutationDto, values: TimeMutationDto, rates: RateDto<BaseTierDto>): string => {
    if (
        values.userId === initialValues.userId &&
        values.fileId === initialValues.fileId &&
        values.billable === initialValues.billable &&
        values.billable === Billable.Billable &&
        values.date === initialValues.date &&
        initialValues.rate
    ) {
        // If nothing of interest has changed from the initial form, inherit the initial value.
        return initialValues.rate
    }
    if (values.billable !== Billable.Billable) {
        return '0.00'
    }

    return resolveRate(rates, values.userId, values.date)
}

const getBillableValue = (
    initialValues: TimeMutationDto,
    values: TimeMutationDto,
    rates: RateDto<BaseTierDto>,
    timeCategories: TimeCategory[],
): Billable => {
    if (values.fileId === initialValues.fileId && values.category === initialValues.category && initialValues.billable) {
        // If nothing of interest has changed from the initial form, inherit the initial value.
        return initialValues.billable
    }

    const category = timeCategories?.find(category => category.key === values.category)
    return category?.billable ?? rates?.billable ?? values.billable ?? initialValues.billable
}

export default function TimeMutationDialogBodyContent(
    props: HammockFormDialogProps<TimeMutationDto, BaseMutableOverviewDialogProps<TimeOverviewDto>> & {
        focusTrigger?: number
        updateFocusTrigger?: () => void
    },
): ReactElement {
    const axios = useAxios()
    const modules = useModules()

    const roles = useRoles()
    const { settings } = useSettings()
    const styles = useStyles()
    const formikFieldStyles = useFormikFieldStyles()
    const timeDivisorSetting = useTimeDivisorSetting()
    const { t: translate } = useTranslation()
    const { isViewport, viewport } = useViewport()

    const { initialValues, values, setFieldValue } = useFormikContext<TimeMutationDto>()

    const { applyDerivedValues, onKeyDownCapture } = useDerivedFormValues<TimeMutationDto>()

    const previousValues = usePrevious<TimeMutationDto>(values)

    const dialogBodyProps = useMemo(() => props.dialogFormBodyProps?.() ?? ({} as BaseMutableOverviewDialogProps<TimeOverviewDto>), [props])

    const {
        hasTimeCategoriesModule: fileHasTimeCategoriesModule,
        timeCategories,
        defaultCategory: defaultTimeCategory,
        setCurrentFile: setTimeCategoriesFile,
    } = useTimeCategoriesSetting()

    const timeCategoryOptions = useMemo<Map<string, string>>(
        () => new Map(timeCategories?.map(x => [x.key, x.name] as [string, string])),
        [timeCategories],
    )

    const hasTimeCategoriesModule = useMemo<boolean>(
        () =>
            (fileHasTimeCategoriesModule || settings?.modules?.find(mod => mod.type === ModuleType.TimeCategories)?.defaultActiveForFile) &&
            settings?.modules?.some(mod => mod.type === ModuleType.TimeCategories),
        [fileHasTimeCategoriesModule, settings],
    )

    const viewportSmall = useMemo<boolean>(() => isViewport('<=', Viewport.OutlookNew), [viewport])

    const isInvoiceDraft: boolean = dialogBodyProps.item?.invoice?.status === InvoiceStatus.Draft || false
    const isInvoiceFinal: boolean =
        dialogBodyProps.item?.invoice?.status !== undefined && dialogBodyProps.item?.invoice?.status !== InvoiceStatus.Draft

    const {
        fileModule: budgetMonitoringModule,
        has: hasBudgetMonitoringModule,
        setCurrentFile: setBudgetMonitoringModuleFile,
    } = useFileModule<BudgetMonitoringModuleDto>(ModuleType.BudgetMonitoring)

    // The rate of the file, undefined for as long as it is unknown - either because there is no file yet or because
    // the request for it is still running. Nothing is derived from it while it is undefined: a rate written before the
    // file's rate is known is a 0,00 that useDerivedFormValues turns into the new baseline, after which getRateValue
    // keeps inheriting that 0,00 and the real rate never lands (HAM-4308).
    const [fileRates, setFileRates] = useState<RateDto<BaseTierDto>>(undefined)
    // Without a file there is nothing to look up and the scaffold from the settings decides.
    const scaffoldRates = useMemo<RateDto<BaseTierDto>>(
        () => ({
            fileRateType: settings?.fileScaffold?.fileRate?.type || FileRateType.File,
            rate: settings?.fileScaffold?.fileRate?.hourRate || '0.00',
            currency: settings?.fileScaffold?.currency || 'EUR',
        }),
        [settings],
    )
    const rates = useMemo<RateDto<BaseTierDto>>(() => (values.fileId ? fileRates : scaffoldRates), [values.fileId, fileRates, scaffoldRates])
    const { currency } = useCurrency(values.currency)
    const currencySymbol = useMemo<string>(() => currency?.symbol ?? currency?.alpha ?? '¤', [currency])
    const [budgetMonitoring, setBudgetMonitoring] = useState<BudgetMonitoringDto>(undefined)

    //Check if the category field should use the initial value or live value
    const useInitialCategory = useMemo<boolean>(
        () => !!initialValues.category && initialValues.fileId === values.fileId,
        [initialValues, values.fileId],
    )
    const useInitialDescription = useMemo<boolean>(
        () => !!initialValues.description && initialValues.fileId === values.fileId && initialValues.category === values.category,
        [initialValues, values.fileId, values.category],
    )
    //Check if the billable field should use the initial value or calculated value
    const useInitialBillable = useMemo<boolean>(
        () => !!initialValues.billable && initialValues.fileId === values.fileId && initialValues.category === values.category,
        [initialValues, values.fileId, values.category],
    )

    const fileFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const categoryFocusRef: IRefObject<HTMLInputElement> = useRef<HTMLInputElement>()
    const descriptionFocusRef: RefObject<ITextField> = useRef<ITextField>()
    const durationFocusRef: RefObject<ITextField> = useRef<ITextField>()

    useEffect(() => {
        applyDerivedValues({
            rate: initialValues.rate,
            rateTotal: multiplyTimeNumber(initialValues.duration, initialValues.rate, '0.00', 'en', 'en'),
            billable: initialValues.billable,
        })
    }, [initialValues])

    useEffect(() => {
        if (previousValues && previousValues?.fileId !== values.fileId && !!values.category) {
            setFieldValue('category', undefined)
            return
        }
        if (isInvoiceDraft || isInvoiceFinal) {
            return
        }

        if (!values.fileId) {
            fileFocusRef.current?.focus()
        } else if (!!categoryFocusRef.current && !values.category) {
            categoryFocusRef.current?.focus()
        } else if (!!initialValues.description || !values.description) {
            //Focus on description when in edit mode or when empty in create mode.
            descriptionFocusRef.current?.focus()
        } else {
            durationFocusRef.current?.focus()
        }
    }, [values.fileId, values.category, props.focusTrigger, hasTimeCategoriesModule])

    useEffect(() => {
        applyDerivedValues({ rateTotal: multiplyTimeNumber(values.duration, values.rate, '0.00', 'en', 'en') })
    }, [values.rate, values.duration])

    useEffect(() => {
        if (values.fileId) {
            axios.get<IFileDto>(`/file/${values.fileId}`).then(response => {
                setBudgetMonitoringModuleFile(response.data)
                setTimeCategoriesFile(response.data)
            })
        }
    }, [values.fileId])

    useEffect(() => {
        // Drop the rate of the previous file first, so nothing is derived from it while the new one is on its way.
        setFileRates(undefined)
        if (values.fileId) {
            // Fetch the rate when the file has changed.
            axios.get<RateDto<BaseTierDto>>(`/file/${values.fileId}/rate`).then(response => {
                setFileRates(response.data)
            })
        }
    }, [values.fileId])

    // Whether the registration is billable decides whether there is a rate at all. The form only learns it a render
    // after the rate arrives, so the rate below would first be computed with an unknown flag and land on 0,00 - which
    // useDerivedFormValues then makes the baseline the rate keeps being inherited from (HAM-4308). It is therefore
    // derived here as well, and used for the rate as long as the form itself has no value yet.
    const derivedBillable = useMemo<Billable>(() => {
        if (!rates) {
            return values.billable
        }
        return useInitialBillable ? initialValues.billable : getBillableValue(initialValues, values, rates, timeCategories)
    }, [rates, initialValues, values.fileId, values.category, values.billable, useInitialBillable, timeCategories])

    useEffect(() => {
        if (rates) {
            applyDerivedValues({
                // What the user picked wins; the derived value only fills the gap before the field has been set.
                rate: getRateValue(initialValues, { ...values, billable: values.billable ?? derivedBillable }, rates),
                currency: rates.currency,
            })
        }
        // The duration is deliberately not a dependency: it does not change which rate applies, and recomputing on it
        // threw away a rate the user had typed themselves the moment they corrected the duration afterwards.
    }, [rates, values.userId, values.billable, derivedBillable, values.date])

    useEffect(() => {
        if (rates) {
            applyDerivedValues({ billable: derivedBillable })
        }
    }, [rates, values.fileId, values.category])

    useEffect(() => {
        if (hasBudgetMonitoringModule) {
            // Fetch the outstanding times when the file has changed.
            axios.get<BudgetMonitoringDto>(`/file/${values.fileId}/budget/monitoring`).then(response => {
                setBudgetMonitoring(response.data)
            })
        }
    }, [hasBudgetMonitoringModule])

    const exceedsBudget = useMemo<boolean>(() => {
        let currentAmount = 0
        if (budgetMonitoringModule && budgetMonitoringModule.hourThreshold > 0 && budgetMonitoring && values.duration) {
            currentAmount = budgetMonitoring.outstandingHours
            if (budgetMonitoringModule.includeBilled) {
                currentAmount += budgetMonitoring.billedHours
            }

            currentAmount += Math.max(0, parseTimeInMinutes(values.duration) / 60 - parseTimeInMinutes(initialValues?.duration || '0:00') / 60)
            if (currentAmount > budgetMonitoringModule.hourThreshold) {
                return true
            }
        }

        currentAmount = 0
        if (budgetMonitoringModule && parseNumber(budgetMonitoringModule.priceThreshold, 'en') > 0 && budgetMonitoring && values.duration) {
            if (budgetMonitoringModule.includeHours) {
                currentAmount += parseNumber(budgetMonitoring.outstandingHoursPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedHoursPrice, 'en')
                }
            }
            if (budgetMonitoringModule.includeTravels) {
                currentAmount += parseNumber(budgetMonitoring.outstandingTravelsPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedTravelsPrice, 'en')
                }
            }
            if (budgetMonitoringModule.includeDisbursements) {
                currentAmount += parseNumber(budgetMonitoring.outstandingDisbursementsPrice, 'en')
                if (budgetMonitoringModule.includeBilled) {
                    currentAmount += parseNumber(budgetMonitoring.billedDisbursementsPrice, 'en')
                }
            }

            currentAmount += Math.max(
                0,
                multiplyTime(values.duration, parseNumber(values.rate, 'en')) -
                    multiplyTime(initialValues?.duration || '0:00', parseNumber(initialValues?.rate || '0.00', 'en')),
            )

            return currentAmount > parseNumber(budgetMonitoringModule.priceThreshold, 'en')
        }

        return false
    }, [hasBudgetMonitoringModule, budgetMonitoring, values.duration])

    const durationDeviatesFromTimeDivisor = useMemo<boolean>(() => {
        if (!timeDivisorSetting?.timeDivisor || timeDivisorSetting?.timeDivisor < 2) {
            return false
        }

        const time = parseTimeInMinutes(values.duration)
        return time > 0 && time % settings?.timeDivisor > 0
    }, [values.duration, settings])

    useEffect(() => {
        props.setValidationSchema?.(getValidationSchema(hasTimeCategoriesModule, translate))

        if (hasTimeCategoriesModule) {
            const defaultCategory = defaultTimeCategory(initialValues.emailInbound)
            const currentCategoryKey = useInitialCategory ? initialValues.category : (values.category ?? defaultCategory?.key)
            applyDerivedValues({ category: currentCategoryKey })
        } else {
            applyDerivedValues({ category: useInitialCategory ? initialValues.category : undefined })
        }
    }, [hasTimeCategoriesModule, timeCategories])

    useEffect(() => {
        let description = values.description
        if (useInitialDescription) {
            description = initialValues.description
        } else {
            const currentCategory = (timeCategories || []).find(cat => cat.key === values.category)
            if (currentCategory?.defaultDescription && currentCategory?.appendDescription && values.description?.length > 0) {
                description = `${values.description} / ${currentCategory?.defaultDescription}`
            } else if (currentCategory?.defaultDescription) {
                description = currentCategory?.defaultDescription
            }
        }

        applyDerivedValues({ description })
    }, [hasTimeCategoriesModule, timeCategories, values.category])

    const durationSuggestions = useMemo<string[]>(() => {
        if (!timeDivisorSetting.timeDivisor || timeDivisorSetting.timeDivisor < 2) {
            return []
        }

        return [
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 2),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 3),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 4),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 5),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 6),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 7),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 8),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 9),
            formatTimeInMinutes(timeDivisorSetting.timeDivisor * 10),
        ]
    }, [timeDivisorSetting])

    const formWarning = dialogBodyProps.formWarning?.(values)

    return (
        <DialogContent onKeyDownCapture={onKeyDownCapture}>
            <FormContainer>
                {props.unsavedWarning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('base.unsaved.dialog')}</MessageBarBody>
                    </MessageBar>
                )}
                {formWarning && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{formWarning}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceDraft && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('time.edit.form.invoice.draft')}</MessageBarBody>
                    </MessageBar>
                )}
                {isInvoiceFinal && (
                    <MessageBar intent={'error'} layout={'multiline'}>
                        <MessageBarBody>{translate('time.edit.form.invoice.final')}</MessageBarBody>
                    </MessageBar>
                )}
                {budgetMonitoringModule && budgetMonitoring && (
                    <>
                        {exceedsBudget && (
                            <MessageBar intent={'error'} layout={'multiline'}>
                                <MessageBarBody>{translate('time.edit.form.budget.hours.exceeds')}</MessageBarBody>
                            </MessageBar>
                        )}
                        <MessageBar intent={'info'} layout={'multiline'}>
                            <MessageBarBody>
                                <div
                                    dangerouslySetInnerHTML={{
                                        __html: [
                                            translate('time.edit.form.budget.info.title'),
                                            budgetMonitoringModule.includeHours && budgetMonitoringModule.includeBilled
                                                ? translate('time.edit.form.budget.info.billedhours', {
                                                      billedHours: parseTimeInHours(budgetMonitoring.billedHours),
                                                      billedHoursPrice: formatInlineCurrency(budgetMonitoring.billedHoursPrice, currency),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeHours
                                                ? translate('time.edit.form.budget.info.outstandinghours', {
                                                      outstandingHours: parseTimeInHours(budgetMonitoring.outstandingHours),
                                                      outstandingHoursPrice: formatInlineCurrency(budgetMonitoring.outstandingHoursPrice, currency),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeTravels && budgetMonitoringModule.includeBilled
                                                ? translate('time.edit.form.budget.info.billedtravels', {
                                                      ...budgetMonitoring,
                                                      billedTravelsPrice: formatInlineCurrency(budgetMonitoring.billedTravelsPrice, currency),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeTravels
                                                ? translate('time.edit.form.budget.info.outstandingtravels', {
                                                      ...budgetMonitoring,
                                                      outstandingTravelsPrice: formatInlineCurrency(
                                                          budgetMonitoring.outstandingTravelsPrice,
                                                          currency,
                                                      ),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeDisbursements && budgetMonitoringModule.includeBilled
                                                ? translate('time.edit.form.budget.info.billeddisbursements', {
                                                      ...budgetMonitoring,
                                                      billedDisbursementsPrice: formatInlineCurrency(
                                                          budgetMonitoring.billedDisbursementsPrice,
                                                          currency,
                                                      ),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.includeDisbursements
                                                ? translate('time.edit.form.budget.info.outstandingdisbursements', {
                                                      ...budgetMonitoring,
                                                      outstandingDisbursementsPrice: formatInlineCurrency(
                                                          budgetMonitoring.outstandingDisbursementsPrice,
                                                          currency,
                                                      ),
                                                  })
                                                : undefined,
                                            budgetMonitoringModule.hourThreshold > 0
                                                ? translate('time.edit.form.budget.info.hourthreshold', {
                                                      ...budgetMonitoringModule,
                                                  })
                                                : undefined,
                                            parseNumber(budgetMonitoringModule.priceThreshold, 'en') > 0
                                                ? translate('time.edit.form.budget.info.pricethreshold', {
                                                      ...budgetMonitoringModule,
                                                      priceThreshold: formatInlineCurrency(budgetMonitoringModule.priceThreshold, currency),
                                                  })
                                                : undefined,
                                        ]
                                            .filter(Boolean)
                                            .join('<br/>'),
                                    }}
                                ></div>
                            </MessageBarBody>
                        </MessageBar>
                    </>
                )}
                {durationDeviatesFromTimeDivisor && (
                    <MessageBar intent={'warning'} layout={'multiline'}>
                        <MessageBarBody>{translate('time.edit.form.divisor', { divisor: settings?.timeDivisor })}</MessageBarBody>
                    </MessageBar>
                )}

                <FormikComboboxField<TimeMutationDto, FileComboboxProps>
                    required
                    fieldName={'fileId'}
                    component={FileCombobox}
                    placeholderKey={'registration.fileid'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || isInvoiceDraft,
                        disabled: isInvoiceFinal || isInvoiceDraft,
                        componentRef: fileFocusRef,
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                {hasTimeCategoriesModule && (
                    <FormikComboboxField<TimeMutationDto, EnumMapComboboxProps<string>>
                        required={hasTimeCategoriesModule}
                        fieldName={'category'}
                        placeholderKey={'time.category.label'}
                        component={EnumMapCombobox<string>}
                        componentProps={{
                            placeholderPrefixKey: '',
                            values: timeCategoryOptions,
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal || !values.fileId,
                            componentRef: categoryFocusRef,
                            onKeyDown: e => {
                                if (e.key === 'Tab') {
                                    e.preventDefault()
                                    props.updateFocusTrigger()
                                }
                            },
                        }}
                    />
                )}
                <FormikTextareaField<TimeMutationDto>
                    fieldName="description"
                    placeholderKey={'registration.description'}
                    componentProps={{
                        required: true,
                        readOnly: isInvoiceFinal,
                        disabled: isInvoiceFinal,
                        ref: descriptionFocusRef,
                    }}
                    className={formikFieldStyles.tinyLabel}
                />
                <FormikComboboxField<TimeMutationDto, SubComboboxProps<UserListItemDto>>
                    required
                    fieldName={'userId'}
                    component={UserCombobox}
                    placeholderKey={'registration.userid'}
                    componentProps={{
                        placeholderPrefixKey: '',
                        readOnly: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        disabled: isInvoiceFinal || !roles.any(rolesWriteAllTimes),
                        onKeyDown: e => {
                            if (e.key === 'Tab') {
                                e.preventDefault()
                                props.updateFocusTrigger()
                            }
                        },
                    }}
                />
                {durationSuggestions.length > 0 && !(isInvoiceFinal || isInvoiceDraft) && (
                    <OverflowButtonPanel
                        data={durationSuggestions}
                        overflowAxis={'horizontal'}
                        onItemClick={value => setFieldValue('duration', value)}
                    />
                )}
                <div className={styles.rateRow}>
                    <FormikDurationField<TimeMutationDto>
                        required
                        fieldName={'duration'}
                        placeholderKey={'time.duration.label'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            ref: durationFocusRef,
                        }}
                    />
                    {modules.has(ModuleType.Invoices) && (
                        <>
                            <FormikMoneyField<TimeMutationDto>
                                required
                                fieldName={'rate'}
                                placeholderKey={'time.rate'}
                                componentProps={{
                                    readOnly: isInvoiceFinal || values.billable !== Billable.Billable,
                                    disabled: isInvoiceFinal || values.billable !== Billable.Billable,
                                    contentBefore: currencySymbol,
                                }}
                            />
                            <FormikMoneyField<TimeMutationDto>
                                required
                                fieldName={'rateTotal'}
                                placeholderKey={'time.rate'}
                                componentProps={{
                                    readOnly: true,
                                    disabled: isInvoiceFinal,
                                    tabIndex: -1,
                                    appearance: 'filled-darker',
                                    contentBefore: currencySymbol,
                                }}
                            />
                        </>
                    )}
                </div>
                <div className={mergeClasses(styles.dateRow, viewportSmall && styles.dateRowSmall)}>
                    <FormikDatePickerField<TimeMutationDto>
                        required
                        fieldName={'date'}
                        placeholderKey={'registration.date'}
                        componentProps={{
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                        }}
                    />
                    <FormikComboboxField<TimeMutationDto, BillableComboboxProps>
                        required
                        fieldName={'billable'}
                        placeholderKey={'time.billable'}
                        component={BillableCombobox}
                        componentProps={{
                            placeholderPrefixKey: '',
                            readOnly: isInvoiceFinal,
                            disabled: isInvoiceFinal,
                            onKeyDown: e => {
                                if (e.key === 'Tab') {
                                    e.preventDefault()
                                    props.updateFocusTrigger()
                                }
                            },
                        }}
                        className={!viewportSmall && styles.billable}
                    />
                </div>
                {/*Source - https://stackoverflow.com/a/477699*/}
                <input type="submit" hidden />
            </FormContainer>
        </DialogContent>
    )
}
